/**
 * Sincronización local ⇄ planilla.
 *
 * El orden es **push y después pull**, siempre. Al revés, un pull podría traer la versión vieja
 * de una fila que todavía no subió y pisar la corrección del usuario.
 *
 * El pull reemplaza cada tabla completa (así se propagan también los borrados hechos desde la
 * planilla) y después vuelve a aplicar encima las filas locales sucias. Sin ese último paso,
 * sincronizar mientras hay cambios pendientes los perdería — que es exactamente el momento en
 * que la app tiene que ser más cuidadosa, porque el usuario está en medio de una sesión.
 *
 * La resolución de conflictos es a nivel de **fila individual** (§2), no de sesión completa: dos
 * dispositivos editando series distintas de la misma sesión no se pisan, porque cada serie es
 * una fila con su propia clave.
 */

import {
  MAPEO_EJERCICIO,
  MAPEO_GIMNASIO,
  MAPEO_PERFIL,
  TAB_CARDIO,
  TAB_COMIDAS,
  TAB_DIAS_BLOQUEADOS,
  TAB_EJERCICIOS_PROPIOS,
  TAB_INGESTAS,
  TAB_MEDIDAS,
  TAB_PERFIL,
  TAB_PLANES,
  TAB_RUTINAS,
  TAB_RUTINA_DIAS,
  TAB_RUTINA_EJERCICIOS,
  TAB_SERIES,
  TAB_SESIONES,
  identidad,
  leerTabla,
  type DefinicionTab,
  type Mapeo,
} from '@mg/sheets';
import type { Ejercicio, Gimnasio, Maquina } from '@mg/core';
import type { Table } from 'dexie';

import { ErrorAPI, estaEnLinea, pedir } from './api.ts';
import { TABLA_DEXIE, db, escribirMeta, leerMeta, type TablaSync } from './db.ts';

interface Definicion {
  sync: TablaSync;
  tab: DefinicionTab;
  mapeo: Mapeo<Record<string, unknown>>;
}

const DEFINICIONES: Definicion[] = [
  { sync: 'perfil', tab: TAB_PERFIL, mapeo: MAPEO_PERFIL as unknown as Mapeo<Record<string, unknown>> },
  {
    sync: 'ejercicios_propios',
    tab: TAB_EJERCICIOS_PROPIOS,
    mapeo: MAPEO_EJERCICIO as unknown as Mapeo<Record<string, unknown>>,
  },
  { sync: 'planes', tab: TAB_PLANES, mapeo: identidad() },
  { sync: 'dias_bloqueados', tab: TAB_DIAS_BLOQUEADOS, mapeo: identidad() },
  { sync: 'rutinas', tab: TAB_RUTINAS, mapeo: identidad() },
  { sync: 'rutina_dias', tab: TAB_RUTINA_DIAS, mapeo: identidad() },
  { sync: 'rutina_ejercicios', tab: TAB_RUTINA_EJERCICIOS, mapeo: identidad() },
  { sync: 'sesiones', tab: TAB_SESIONES, mapeo: identidad() },
  { sync: 'series', tab: TAB_SERIES, mapeo: identidad() },
  { sync: 'cardio', tab: TAB_CARDIO, mapeo: identidad() },
  { sync: 'medidas', tab: TAB_MEDIDAS, mapeo: identidad() },
  { sync: 'comidas', tab: TAB_COMIDAS, mapeo: identidad() },
  { sync: 'ingestas_imagen', tab: TAB_INGESTAS, mapeo: identidad() },
];

interface RespuestaPull {
  servidor: { ahora: string };
  compartido: { gimnasios: Gimnasio[]; ejercicios: Ejercicio[]; maquinas: Maquina[] };
  personal: Record<string, string[][]>;
}

interface RespuestaPush {
  ok: boolean;
  resultados: Record<string, { insertadas: number; actualizadas: number; sinCambios: number }>;
  rechazadas: { tabla: string; clave: string; motivo: string }[];
}

export interface ResultadoSync {
  subidas: number;
  bajadas: number;
  rechazadas: { tabla: string; clave: string; motivo: string }[];
  en: string;
}

const tablaDe = (sync: TablaSync): Table<Record<string, unknown>, string> =>
  db[TABLA_DEXIE[sync]] as unknown as Table<Record<string, unknown>, string>;

// ---------------------------------------------------------------------------
// Push
// ---------------------------------------------------------------------------

export async function push(): Promise<{ subidas: number; rechazadas: RespuestaPush['rechazadas'] }> {
  const sucios = await db.sucios.toArray();
  if (sucios.length === 0) return { subidas: 0, rechazadas: [] };

  const tablas: Record<string, Record<string, unknown>[]> = {};
  const enviados: string[] = [];

  for (const s of sucios) {
    const fila = await tablaDe(s.tabla).get(s.clave);
    if (!fila) {
      // La fila se borró en local: no hay nada que subir, se saca de la cola.
      enviados.push(s.id);
      continue;
    }
    // El push aplica `aFila`, espejo del `deFila` del pull. Sin esta simetría, las tablas con
    // mapeo real suben la entidad cruda y el servidor la serializa a ciegas: el objetivo del
    // perfil llegaba a la celda como "[object Object]" y un ejercicio propio perdía su rango.
    const def = DEFINICIONES.find((d) => d.sync === s.tabla);
    (tablas[s.tabla] ??= []).push(def ? def.mapeo.aFila(fila) : fila);
    enviados.push(s.id);
  }

  if (Object.keys(tablas).length === 0) {
    await db.sucios.bulkDelete(enviados);
    return { subidas: 0, rechazadas: [] };
  }

  const respuesta = await pedir<RespuestaPush>('/sync/push', { metodo: 'POST', cuerpo: { tablas } });

  // Las rechazadas se quedan en la cola con el motivo, para poder mostrárselo al usuario. Que
  // el servidor rechace un valor imposible no debe borrar el registro local en silencio: es un
  // dato que la persona tiene que revisar.
  const rechazadas = respuesta.rechazadas ?? [];
  const clavesRechazadas = new Set(rechazadas.map((r) => `${r.tabla}:${r.clave}`));

  await db.transaction('rw', db.sucios, async () => {
    await db.sucios.bulkDelete(enviados.filter((id) => !clavesRechazadas.has(id)));
    for (const r of rechazadas) {
      const id = `${r.tabla}:${r.clave}`;
      const previo = await db.sucios.get(id);
      if (previo) {
        await db.sucios.put({ ...previo, intentos: previo.intentos + 1, ultimoError: r.motivo });
      }
    }
  });

  const subidas = Object.values(respuesta.resultados ?? {}).reduce(
    (a, r) => a + r.insertadas + r.actualizadas,
    0,
  );
  return { subidas, rechazadas };
}

// ---------------------------------------------------------------------------
// Pull
// ---------------------------------------------------------------------------

export async function pull(): Promise<number> {
  const datos = await pedir<RespuestaPull>('/sync/pull');
  let bajadas = 0;

  // El catálogo global y los ejercicios propios viven en la misma tabla local.
  const globales = datos.compartido.ejercicios ?? [];

  // Todo el parseo ocurre ANTES de la transacción: adentro de una transacción Dexie no puede
  // haber awaits ajenos a Dexie (la corta y auto-commitea).
  const porTabla = DEFINICIONES.flatMap((def) => {
    const crudo = datos.personal[def.tab.nombre];
    if (!crudo) return [];
    const { filas } = leerTabla<Record<string, unknown>>(crudo, def.tab);
    return [{ def, delServidor: filas.map((f) => def.mapeo.deFila(f)) }];
  });

  // UNA transacción para el pull entero, no una por tabla. Con transacciones separadas el
  // liveQuery de useDatos emitía entre tabla y tabla, y cualquier pantalla podía ver un estado
  // a medias — perfil ya bajado, rutina todavía vacía — que se lee igual que una cuenta sin
  // configurar: así se disparaba el onboarding para usuarios que ya tenían todo. Con una sola
  // transacción hay una sola emisión, la del commit.
  const tablas = [...new Set([db.gimnasios, db.maquinas, db.sucios, ...porTabla.map(({ def }) => tablaDe(def.sync))])];
  await db.transaction('rw', tablas, async () => {
    // Catálogo compartido: llega ya parseado y no tiene versión local sucia posible (el mapa de
    // máquinas se aporta por su propio endpoint).
    await db.gimnasios.clear();
    await db.gimnasios.bulkPut(datos.compartido.gimnasios ?? []);
    await db.maquinas.clear();
    await db.maquinas.bulkPut(datos.compartido.maquinas ?? []);

    for (const { def, delServidor } of porTabla) {
      const tabla = tablaDe(def.sync);
      const sucios = await db.sucios.where('tabla').equals(def.sync).toArray();
      const localesSucias = (await Promise.all(sucios.map((s) => tabla.get(s.clave)))).filter(
        (x): x is Record<string, unknown> => x !== undefined,
      );

      if (def.sync === 'ejercicios_propios') {
        // Esta tabla local mezcla catálogo global y ejercicios propios: se reconstruye con los
        // dos, en vez de vaciarla y perder el catálogo.
        await tabla.clear();
        await tabla.bulkPut(globales as unknown as Record<string, unknown>[]);
        await tabla.bulkPut(delServidor);
      } else {
        await tabla.clear();
        await tabla.bulkPut(delServidor);
      }
      // Lo local pendiente vuelve encima: si no, sincronizar en medio de una sesión borraría
      // las series que todavía no subieron.
      if (localesSucias.length > 0) await tabla.bulkPut(localesSucias);
    }
  });

  bajadas += (datos.compartido.gimnasios?.length ?? 0) + (datos.compartido.maquinas?.length ?? 0);
  for (const { delServidor } of porTabla) bajadas += delServidor.length;

  await escribirMeta('ultimoPull', new Date().toISOString());
  return bajadas;
}

// ---------------------------------------------------------------------------
// Orquestación
// ---------------------------------------------------------------------------

let enCurso: Promise<ResultadoSync> | null = null;

export function sincronizar(): Promise<ResultadoSync> {
  // Un solo sync a la vez: dos concurrentes se pisarían al reemplazar tablas.
  enCurso ??= ejecutar().finally(() => {
    enCurso = null;
  });
  return enCurso;
}

async function ejecutar(): Promise<ResultadoSync> {
  if (!estaEnLinea()) {
    throw new ErrorAPI('Sin conexión. Tus datos están guardados en el teléfono.', 0, 'sin_red');
  }

  const { subidas, rechazadas } = await push();
  const bajadas = await pull();
  const en = new Date().toISOString();
  await escribirMeta('ultimoSync', en);
  return { subidas, bajadas, rechazadas, en };
}

export async function pendientes(): Promise<number> {
  return db.sucios.count();
}

export async function ultimoSync(): Promise<string | null> {
  return leerMeta<string | null>('ultimoSync', null);
}

/**
 * Sincroniza cuando vuelve la conexión y cuando la app vuelve al frente. No hay intervalo
 * periódico: gastar batería y datos consultando cada minuto no aporta nada en una app de un solo
 * usuario, y §9 dice que la sincronización es un detalle que el usuario no debe percibir.
 */
export function engancharSincronizacionAutomatica(alTerminar?: (r: ResultadoSync | null) => void): () => void {
  const intentar = () => {
    if (!estaEnLinea()) return;
    void sincronizar()
      .then((r) => alTerminar?.(r))
      .catch(() => alTerminar?.(null));
  };

  const alVolverAlFrente = () => {
    if (document.visibilityState === 'visible') intentar();
  };

  window.addEventListener('online', intentar);
  document.addEventListener('visibilitychange', alVolverAlFrente);

  return () => {
    window.removeEventListener('online', intentar);
    document.removeEventListener('visibilitychange', alVolverAlFrente);
  };
}
